import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  TextField,
  Button,
  DataTable,
  Badge,
  Box,
  SkeletonBodyText,
  EmptyState,
} from '@shopify/polaris';
import reportsApi from '@/services/reports';

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

function formatCurrency(dollars: number): string {
  return `$${Number(dollars).toFixed(2)}`;
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

type BadgeTone = 'success' | 'warning' | 'critical' | 'info' | undefined;

function statusTone(status: string): BadgeTone {
  switch (status?.toLowerCase()) {
    case 'in_use':
    case 'active':
      return 'success';
    case 'returned':
    case 'complete':
      return 'info';
    case 'overdue':
      return 'critical';
    case 'pending':
    case 'processing':
      return 'warning';
    default:
      return undefined;
  }
}

export default function ConsignmentReport() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['report', 'consignment', startDate, endDate],
    queryFn: () => reportsApi.getConsignment({ startDate, endDate }),
  });

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      await reportsApi.downloadCsv('/reports/consignment', { startDate, endDate });
    } finally {
      setExporting(false);
    }
  };

  // API returns data as a flat array of consignment objects
  const items = Array.isArray(data?.data) ? data.data : [];

  const rows =
    items.map((item) => [
      item.orderNumber,
      item.clinicName,
      item.patientName,
      item.productName,
      formatDate(item.startDate),
      formatDate(item.endDate),
      item.weeks,
      formatCurrency(item.amount),
      <Badge key={item.orderNumber} tone={statusTone(item.status)}>
        {item.status}
      </Badge>,
    ]);

  return (
    <Page
      title="Consignment"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
    >
      <Card>
        <BlockStack gap="400">
          <InlineStack gap="400" blockAlign="end">
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={setStartDate}
              autoComplete="off"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={setEndDate}
              autoComplete="off"
            />
            <Box paddingBlockStart="600">
              <InlineStack gap="200">
                <Button onClick={() => refetch()}>Refresh</Button>
                <Button onClick={handleExport} loading={exporting}>
                  Export CSV
                </Button>
              </InlineStack>
            </Box>
          </InlineStack>

          {isLoading ? (
            <SkeletonBodyText lines={8} />
          ) : isError ? (
            <EmptyState heading="Failed to load report" image="">
              <p>There was an error loading the Consignment report. Try refreshing.</p>
            </EmptyState>
          ) : !data ? (
            <EmptyState heading="No data" image="">
              <p>No consignment data found for the selected date range.</p>
            </EmptyState>
          ) : (
            <DataTable
              columnContentTypes={[
                'text',
                'text',
                'text',
                'text',
                'text',
                'text',
                'numeric',
                'text',
                'text',
              ]}
              headings={[
                'Order #',
                'Clinic',
                'Patient',
                'Product',
                'Start',
                'End',
                'Weeks',
                'Amount',
                'Status',
              ]}
              rows={rows}
              footerContent={rows.length === 0 ? 'No records found' : undefined}
            />
          )}
        </BlockStack>
      </Card>
    </Page>
  );
}
